import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, DownloadIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/ui/CtaBand';
import { NotFound } from './NotFound';
import { policies } from '../data/policies';
import { getPolicyDetail } from '../data/agenda';
const sections = [{
  id: 'challenge',
  label: 'The challenge'
}, {
  id: 'objective',
  label: 'Our objective'
}, {
  id: 'plan',
  label: 'The plan'
}, {
  id: 'first-100',
  label: 'First 100 days'
}, {
  id: 'targets',
  label: 'First-term targets'
}, {
  id: 'delivery',
  label: 'How we deliver'
}, {
  id: 'funding',
  label: 'Funding logic'
}, {
  id: 'measuring',
  label: 'Measuring progress'
}];
export function PolicyDetail() {
  const {
    slug = ''
  } = useParams();
  const policy = policies.find(item => item.slug === slug);
  const detail = getPolicyDetail(slug);
  if (!policy || !detail) {
    return <NotFound data-id="element-1221" data-mp="3851uqq5qw60y" />;
  }
  const related = detail.related.map(relatedSlug => policies.find(item => item.slug === relatedSlug)).filter(Boolean);
  return <>
      <PageHero eyebrow={policy.category} title={policy.title} intro={detail.proposition} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Agenda',
      to: '/agenda'
    }, {
      label: policy.title
    }]} image={detail.image} imageAlt="" data-id="element-1222" data-mp="2z30wpr2otfy8" />

      <div className="bg-sand-50 py-14 lg:py-20" data-id="element-1223" data-mp="3msmxgiypk38c">
        <div className="mx-auto grid max-w-content gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-1224" data-mp="3muzm1eyz1bud">
          <aside className="lg:col-span-3" data-id="element-1225" data-mp="3i028pvunsgbo">
            <nav aria-label="On this page" className="lg:sticky lg:top-28" data-id="element-1226" data-mp="3d3v88il17x14">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-1227" data-mp="ecthc58agtrt">
                On this page
              </h2>
              <ul className="mt-4 space-y-1 border-l border-sand-300" data-id="element-1228" data-mp="9ll9hdmlxggr">
                {sections.map(section => <li key={section.id} data-id="element-1229" data-mp="sjdhmcqf0pt1">
                    <a href={`#${section.id}`} className="-ml-px block border-l-2 border-transparent py-1.5 pl-4 text-[14px] text-slate-600 transition-colors duration-150 ease-emphasis hover:border-green-600 hover:text-ink" data-id="element-1230" data-mp="xi817gw7iie7">
                      {section.label}
                    </a>
                  </li>)}
              </ul>
              <a href="#main" className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-green-700" data-id="element-1231" data-mp="ergwisn5zg65">
                <DownloadIcon aria-hidden className="h-4 w-4" data-id="element-1232" data-mp="4t13ul8hnyuj" />
                Download this policy (PDF)
              </a>
            </nav>
          </aside>

          <div className="lg:col-span-9" data-id="element-1233" data-mp="1v5ym2he62lsc">
            <article className="space-y-12" data-id="element-1234" data-mp="1z71zyycnm7qx">
              <section id="challenge" aria-labelledby="challenge-h" className="scroll-mt-28" data-id="element-1235" data-mp="1z5qyj56olc2z">
                <h2 id="challenge-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1236" data-mp="3jq4fi3zklzjy">
                  The challenge
                </h2>
                <p className="mt-4 text-[17px] leading-relaxed text-slate-700" data-id="element-1237" data-mp="3floeag0wjgb2">
                  {detail.challenge}
                </p>
              </section>

              <section id="objective" aria-labelledby="objective-h" className="scroll-mt-28" data-id="element-1238" data-mp="3ommiaybcbntn">
                <h2 id="objective-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1239" data-mp="76cxx8wct377">
                  Our objective
                </h2>
                <p className="mt-4 border-l-2 border-green-600 pl-5 font-display text-[20px] leading-relaxed text-ink" data-id="element-1240" data-mp="g8dyofqsq7dm">
                  {detail.objective}
                </p>
              </section>

              <section id="plan" aria-labelledby="plan-h" className="scroll-mt-28" data-id="element-1241" data-mp="2hzh851oy9fwl">
                <h2 id="plan-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1242" data-mp="2i1k5y7wcgdbv">
                  The plan
                </h2>
                <ol className="mt-6 space-y-5" data-id="element-1243" data-mp="2fg2rlhjs4gv8">
                  {detail.plan.map((item, index) => <li key={item.title} data-id="element-1244" data-mp="2jfv23zsqgx38" data-mpi={index}>
                      <Reveal delay={Math.min(index, 4) * 0.03} data-id="element-1245" data-mp="2jiz9f61hw8y4">
                        <div className="rounded-card border border-sand-200 bg-white p-6" data-id="element-1246" data-mp="2ohalzoj6cxk1">
                          <div className="flex items-baseline gap-3" data-id="element-1247" data-mp="2te0y60ruz906">
                            <span className="font-display text-[15px] text-gold-600" data-id="element-1248" data-mp="1vrty946i24u5">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            <h3 className="font-display text-[20px] leading-snug text-ink" data-id="element-1249" data-mp="25prkxn8pntkn">
                              {item.title}
                            </h3>
                          </div>
                          <p className="mt-2.5 pl-8 text-[15px] leading-relaxed text-slate-600" data-id="element-1250" data-mp="1d13u7c570bzp">
                            {item.detail}
                          </p>
                        </div>
                      </Reveal>
                    </li>)}
                </ol>
              </section>

              <section id="first-100" aria-labelledby="first-h" className="scroll-mt-28" data-id="element-1251" data-mp="15grxfkykoszh">
                <div className="rounded-media bg-ink p-7 lg:p-9" data-id="element-1252" data-mp="10hyj5qbgoxy9">
                  <h2 id="first-h" className="font-display text-[26px] leading-tight text-white" data-id="element-1253" data-mp="10kb66hw2tyi6">
                    In the first 100 days
                  </h2>
                  <ul className="mt-5 space-y-3" data-id="element-1254" data-mp="ribpjqoz4wfr">
                    {detail.firstHundredDays.map(item => <li key={item} className="flex gap-3 text-[16px] text-white/85" data-id="element-1255" data-mp="mp8v7gcdhiyi">
                        <CheckIcon aria-hidden className="mt-1 h-4 w-4 shrink-0 text-gold-400" data-id="element-1256" data-mp="hsifwv8e6re5" />
                        {item}
                      </li>)}
                  </ul>
                  <Link to="/first-100-days" className="mt-6 inline-flex items-center gap-2 text-[15px] font-semibold text-gold-300 transition-colors duration-150 ease-emphasis hover:text-gold-100" data-id="element-1257" data-mp="7s9en2zjmfrw">
                    See the full first 100 days
                    <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-1258" data-mp="3ucw8dn1syoj1" />
                  </Link>
                </div>
              </section>

              <section id="targets" aria-labelledby="targets-h" className="scroll-mt-28" data-id="element-1259" data-mp="3lamymzecyz6p">
                <h2 id="targets-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1260" data-mp="3lchisbht2n9q">
                  First-term targets
                </h2>
                <dl className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1261" data-mp="gvdg1qcfcvcu">
                  {detail.firstTermTargets.map(target => <div key={target.label} className="border-t-2 border-green-600 pt-4" data-id="element-1262" data-mp="3378gsnc7wp0">
                      <dt className="sr-only" data-id="element-1263" data-mp="820a9nt9hz9c">{target.label}</dt>
                      <dd data-id="element-1264" data-mp="vsxd8b32xm8g">
                        <span className="block font-display text-[30px] leading-none text-ink" data-id="element-1265" data-mp="vrma8dhgkiie">
                          {target.value}
                        </span>
                        <span className="mt-2 block text-[14px] leading-snug text-slate-600" data-id="element-1266" data-mp="19y6525gtfmc8">
                          {target.label}
                        </span>
                      </dd>
                    </div>)}
                </dl>
              </section>

              <section id="delivery" aria-labelledby="delivery-h" className="scroll-mt-28" data-id="element-1267" data-mp="1epo3alrgf8ti">
                <h2 id="delivery-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1268" data-mp="qztt6byirycy">
                  How we will deliver
                </h2>
                <p className="mt-4 text-[17px] leading-relaxed text-slate-700" data-id="element-1269" data-mp="n0urd0834hpq">
                  {detail.delivery}
                </p>
              </section>

              <section id="funding" aria-labelledby="funding-h" className="scroll-mt-28" data-id="element-1270" data-mp="vyet0a9v0g0v">
                <h2 id="funding-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1271" data-mp="2ccwumxe4zc3f">
                  Funding logic
                </h2>
                <p className="mt-4 text-[17px] leading-relaxed text-slate-700" data-id="element-1272" data-mp="27gpmcxy9poib">{detail.funding}</p>
              </section>

              <section id="measuring" aria-labelledby="measuring-h" className="scroll-mt-28" data-id="element-1273" data-mp="9r11be67q7tk">
                <h2 id="measuring-h" className="font-display text-[28px] leading-tight text-ink" data-id="element-1274" data-mp="eq4750u0oi2l">
                  Measuring progress
                </h2>
                <ul className="mt-5 divide-y divide-sand-300 border-y border-sand-300" data-id="element-1275" data-mp="jif5hz93zvt1">
                  {detail.kpis.map(kpi => <li key={kpi} className="py-4 text-[16px] text-slate-700" data-id="element-1276" data-mp="tf0aikl4p6wv">
                      {kpi}
                    </li>)}
                </ul>
              </section>

              {related.length > 0 && <section aria-labelledby="related-h" data-id="element-1277" data-mp="12fydr0nqtaee">
                  <h2 id="related-h" className="font-display text-[24px] text-ink" data-id="element-1278" data-mp="12dc8j7obr6zc">
                    Related policies
                  </h2>
                  <ul className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-1279" data-mp="3469zexyjjphn">
                    {related.map(item => <li key={item!.slug} data-id="element-1280" data-mp="2woc0fndzjwso">
                        <Link to={`/agenda/${item!.slug}`} className="group flex h-full flex-col justify-between rounded-card border border-sand-200 bg-white p-5 transition-colors duration-200 ease-emphasis hover:border-green-200" data-id="element-1281" data-mp="2rwu0n2np829a">
                          <span className="font-display text-[18px] leading-snug text-ink" data-id="element-1282" data-mp="3fm67hvvjzy8f">
                            {item!.title}
                          </span>
                          <span className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-green-700" data-id="element-1283" data-mp="3ao4rjjtfcf16">
                            Read policy
                            <ArrowRightIcon aria-hidden className="h-3.5 w-3.5 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-1284" data-mp="31lloozpn22ex" />
                          </span>
                        </Link>
                      </li>)}
                  </ul>
                </section>}
            </article>
          </div>
        </div>
      </div>

      <CtaBand title="Hold this policy to its published numbers." description="Get the delivery scorecard for this policy area as soon as it is published." primary={{
      label: 'Join campaign updates',
      to: '/join'
    }} secondary={{
      label: 'Back to the agenda',
      to: '/agenda'
    }} data-id="element-1285" data-mp="nl8oaxc2xrc8" />
    </>;
}